import * as errors from "../../lib/errors";
import * as posActions from "../../lib/posActions";
import * as posModels from "../../lib/api/posModels";
import * as routeHelpers from "../../lib/routeHelpers";
import describeNetworkError from "../../lib/describeNetworkError";
import Drawer from "../Drawer";
import ErrorModal from "../ErrorModal";
import logger from "../../lib/sentryLogger";
import ManagerPermissionForm from "../ManagerPermissionForm";
import OrderDetailsDrawer from "../OrderDetailsDrawer";
import OrderRowItem from "./OrderRowItem";
import React, {useState, useMemo, useEffect, useContext} from "react";
import sortBy from "lodash.sortby";
import styles from "./CashierCheckoutOrders.module.css";
import TextModal from "../TextModal";
import {StationContext} from "../StationContext";
import {useAppDispatch, useAppSelector} from "../../lib/hooks";
import {useNavigate} from "react-router-dom";
import OutlineButton from "../OutlineButton";

const strings = {
    currentOrder: "Current Order :",
    noCurrentOrder: "No Current Order",
    pendingOrders: "Pending Orders",
    noPendingOrders: "No Pending Orders",
    closedOrders: "Closed Orders",
    noClosedOrders: "No Closed Orders",
    confirm: "Confirmation",
    confirmText: "Are you sure you want to cancel this order?",
    goBack: "Go Back",
    submit: "Cancel This Order",
    confirmOpenOrder:
        "Are you sure you want to open this order? It will override any current order data.",
    openOrder: "Open This Order",
    startNewOrder: "Start A New Order",
    reserveOrderError:
        "An unexpected error occurred while reserving a new order id. Please try again",
    pendingOrderOpeningMenuError:
        "An error occurred when processing this order due to a menu mismatch between the kiosk and the POS. Please speak to the guest and start a new order for them.",
    pendingOrderOpeningGenericError:
        "An unexpected error occurred when processing this order. Please speak to the guest and start a new order for them.",
};

type Props = {
    className?: string;
};

export default function CashierCheckoutOrders(props: Props) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {cashierMode, setResetCurrentOrderStateOnCartEmptied, isTraining} =
        useContext(StationContext);

    const [errorMessage, setErrorMessage] = useState("");
    const [cancelOrderId, setCancelOrderId] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [managerId, setManagerId] = useState(
        cashierMode.type === "manager" ? cashierMode.managerId : null,
    );
    const [orderDetails, setOrderDetails] =
        useState<posModels.OrderParsed | null>(null);
    const [openingPendingOrder, setOpeningPendingOrder] = useState(false);

    const stationMode = useAppSelector((state) => state.pos.station.mode);
    const isFrontOfHouse =
        stationMode === "front_of_house_1" ||
        stationMode === "front_of_house_2";
    const posCurrentOrder = useAppSelector((state) => state.pos.currentOrder);
    const currentOrderStoreId = useAppSelector(
        (state) => state.currentOrder.storeId,
    );

    const pendingOrdersById = useAppSelector(
        (state) => state.pos.pendingOrders.byId,
    );
    const sortedPendingOrders = useMemo(() => {
        return sortBy(Object.values(pendingOrdersById), (x) => x.orderDate);
    }, [pendingOrdersById]);

    useEffect(() => {
        if (cashierMode.type !== "manager") {
            setManagerId(null);
        }
    }, [cashierMode.type]);

    function clearCurrentOrder() {
        setResetCurrentOrderStateOnCartEmptied(false);
        dispatch(
            posActions.clearCurrentOrder(
                currentOrderStoreId ?? "",
                "drive_thru",
            ),
        );
    }

    function reserveNewOrderId() {
        setReservingNewOrderId(true);
        return dispatch(posActions.reserveCurrentOrderId(isTraining))
            .then(() => {
                navigate(routeHelpers.menu());
            })
            .catch((err) => {
                logger.warn(err);
                setErrorMessage(strings.reserveOrderError);
            })
            .finally(() => {
                setReservingNewOrderId(false);
            });
    }

    const [reservingNewOrderId, setReservingNewOrderId] = useState(false);
    function returnToMenu() {
        const currentOrderIsPending =
            posCurrentOrder.id &&
            Object.keys(pendingOrdersById).includes(posCurrentOrder.id);

        if (!isFrontOfHouse || !currentOrderIsPending) {
            navigate(routeHelpers.menu());
            return;
        }

        if (currentOrderIsPending) {
            clearCurrentOrder();
        }

        reserveNewOrderId();
    }

    function onCancelOrder() {
        if (cancelling || !cancelOrderId || !managerId) {
            return;
        }

        const cancellingCurrentOrder = cancelOrderId === posCurrentOrder.id;
        if (cancellingCurrentOrder) {
            clearCurrentOrder();
        }

        logger.info("Canceling pending order.", {
            orderId: cancelOrderId,
        });
        setCancelling(true);
        dispatch(posActions.cancelOrder(cancelOrderId))
            .then(() => {
                if (isFrontOfHouse && cancellingCurrentOrder) {
                    return reserveNewOrderId();
                }
            })
            .then(() => {
                if (isFrontOfHouse) {
                    navigate(routeHelpers.menu());
                }
            })
            .catch((err) => {
                const errMsg = describeNetworkError(err).join("\n");
                logger.warn("Failed to cancel pending order.", {
                    orderId: cancelOrderId,
                    errorMessage: errMsg,
                });
                setErrorMessage(errMsg);
            })
            .finally(() => {
                setCancelling(false);
                setCancelOrderId(null);
                if (cashierMode.type !== "manager") {
                    setManagerId(null);
                }
            });
    }

    async function onOpenPendingOrder(order: posModels.OrderParsed) {
        if (openingPendingOrder) {
            return;
        }

        logger.info("Opening pending order.", {
            orderNum: order.orderNumber,
        });
        setOpeningPendingOrder(true);
        setResetCurrentOrderStateOnCartEmptied(false);
        try {
            await dispatch(posActions.openPendingOrder(order));
            if (
                stationMode === "front_of_house_1" ||
                stationMode === "front_of_house_2"
            ) {
                setResetCurrentOrderStateOnCartEmptied(true);
            }
            navigate(routeHelpers.payment());
        } catch (err) {
            let errMessage = order.error
                ? ` Please share the below error with the help desk.\n\n ${order.error}`
                : "";
            if (
                err instanceof Error &&
                err.message === errors.PENDING_ORDER_INVALID_ENTITIES
            ) {
                logger.error("Failed to open pending order.", {
                    orderNum: order.orderNumber,
                    errorMessage: errors.PENDING_ORDER_INVALID_ENTITIES,
                });
                errMessage = strings.pendingOrderOpeningMenuError + errMessage;
                setErrorMessage(errMessage);
            } else {
                logger.error("Failed to open pending order.", {
                    orderNum: order.orderNumber,
                });
                errMessage =
                    strings.pendingOrderOpeningGenericError + errMessage;
                setErrorMessage(errMessage);
            }
        } finally {
            setOpeningPendingOrder(false);
        }
    }

    return (
        <>
            <div className={props.className}>
                {stationMode !== "drive_thru_order_fulfillment" ? (
                    <div className={styles.returnToMenuButtonContainer}>
                        <OutlineButton
                            label={strings.startNewOrder}
                            className={styles.returnToMenuButton}
                            onClick={() => {
                                returnToMenu();
                            }}
                            loading={reservingNewOrderId}
                        />
                    </div>
                ) : null}
                <div className={styles.pendingSection}>
                    <div className={styles.sectionTitle}>
                        {strings.pendingOrders}
                    </div>
                    {sortedPendingOrders.length ? (
                        sortedPendingOrders.map((o) => (
                            <OrderRowItem
                                key={o.id}
                                order={o}
                                onOpen={() => {
                                    onOpenPendingOrder(o);
                                }}
                                onClickDetails={() => setOrderDetails(o)}
                                onCancel={() => setCancelOrderId(o.id)}
                            />
                        ))
                    ) : (
                        <div className={styles.noOrders}>
                            {strings.noPendingOrders}
                        </div>
                    )}
                </div>
            </div>
            {errorMessage ? (
                <ErrorModal
                    errorMessage={errorMessage}
                    onClose={() => setErrorMessage("")}
                    showITInfo
                />
            ) : null}
            {cancelOrderId !== null && managerId ? (
                <TextModal
                    onClose={() => setCancelOrderId(null)}
                    title={strings.confirm}
                    content={strings.confirmText}
                    cancelLabel={strings.goBack}
                    onSubmit={onCancelOrder}
                    submitLabel={strings.submit}
                    submitLoading={cancelling}
                    submitMode="red"
                />
            ) : null}
            {cancelOrderId !== null && !managerId ? (
                <Drawer
                    size="large"
                    onClose={() => {
                        setCancelOrderId(null);
                    }}>
                    <div className={styles.managerPermissionFormContainer}>
                        <ManagerPermissionForm
                            onSuccess={(data) => setManagerId(data.id)}
                        />
                    </div>
                </Drawer>
            ) : null}
            {orderDetails !== null ? (
                <OrderDetailsDrawer
                    order={orderDetails}
                    onClose={() => setOrderDetails(null)}
                />
            ) : null}
        </>
    );
}
